<template>
  <section class="mx-auto max-w-7xl px-6 py-10">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentRoomPage.eyebrow') }}</p>
        <h1 class="mt-2 font-display text-3xl font-semibold text-white">{{ room?.room_name || $t('tournamentRoomPage.roomFallback') }}</h1>
        <p class="mt-2 text-sm text-steel-300">{{ roomStatus }}</p>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <router-link
          to="/tournament"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
        >
          {{ $t('tournamentRoomPage.backToRooms') }}
        </router-link>
      </div>
    </div>

    <p v-if="statusMessage" class="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{{ statusMessage }}</p>
    <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{{ errorMessage }}</p>

    <div class="mt-8 grid gap-6 lg:grid-cols-[1fr,auto,1fr] lg:items-center">
      <div class="rounded-[2rem] border border-white/10 bg-midnight-900/60 p-5">
        <template v-if="leftEntry">
          <div class="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
            <img v-if="leftEntry.photo" :src="leftEntry.photo" :alt="leftEntry.carName" class="aspect-[4/3] w-full object-cover" :style="{ objectPosition: leftEntry.photoPosition }" />
            <div v-else class="flex aspect-[4/3] items-center justify-center text-sm uppercase tracking-[0.3em] text-steel-400">{{ $t('tournamentRoomPage.noPhoto') }}</div>
          </div>
          <div class="mt-5 flex items-center gap-3">
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img v-if="leftEntry.avatar" :src="leftEntry.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ leftEntry.initials }}</div>
            </div>
            <div>
              <p class="text-base font-semibold text-white">{{ leftEntry.displayName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ leftEntry.handle }}</p>
            </div>
          </div>
          <div class="mt-5">
            <p class="text-2xl font-semibold text-white">{{ leftEntry.carName }}</p>
            <p class="mt-3 whitespace-pre-line text-sm leading-6 text-steel-300">{{ leftEntry.specs }}</p>
          </div>
          <div v-if="canEditEntry(leftEntry)" class="mt-4">
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
              :disabled="editSubmitting"
              @click="openEditModal(leftEntry)"
            >
              {{ $t('tournamentRoomPage.editEntry') }}
            </button>
          </div>
          <div class="mt-6 flex items-center justify-between gap-3">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ leftEntry.votes }} {{ $t('tournamentRoomPage.votes') }}</p>
            <button type="button" class="rounded-2xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] transition" :class="voteButtonClass(leftEntry)" :disabled="!canVote(leftEntry)" @click="castVote(leftEntry.id)">
              {{ voteButtonLabel(leftEntry) }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex min-h-[28rem] items-center justify-center rounded-[1.5rem] border border-dashed border-white/10 bg-white/[0.03] p-8 text-center text-sm text-steel-400">
            {{ $t('tournamentRoomPage.slotWaiting1') }}
          </div>
        </template>
      </div>

      <div class="flex items-center justify-center">
        <div class="rounded-full border border-white/10 bg-white/5 px-6 py-5 text-center">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentRoomPage.faceOff') }}</p>
          <p class="mt-2 font-display text-4xl font-semibold text-white">VS</p>
        </div>
      </div>

      <div class="rounded-[2rem] border border-white/10 bg-midnight-900/60 p-5">
        <template v-if="rightEntry">
          <div class="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
            <img v-if="rightEntry.photo" :src="rightEntry.photo" :alt="rightEntry.carName" class="aspect-[4/3] w-full object-cover" :style="{ objectPosition: rightEntry.photoPosition }" />
            <div v-else class="flex aspect-[4/3] items-center justify-center text-sm uppercase tracking-[0.3em] text-steel-400">{{ $t('tournamentRoomPage.noPhoto') }}</div>
          </div>
          <div class="mt-5 flex items-center gap-3">
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img v-if="rightEntry.avatar" :src="rightEntry.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ rightEntry.initials }}</div>
            </div>
            <div>
              <p class="text-base font-semibold text-white">{{ rightEntry.displayName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ rightEntry.handle }}</p>
            </div>
          </div>
          <div class="mt-5">
            <p class="text-2xl font-semibold text-white">{{ rightEntry.carName }}</p>
            <p class="mt-3 whitespace-pre-line text-sm leading-6 text-steel-300">{{ rightEntry.specs }}</p>
          </div>
          <div v-if="canEditEntry(rightEntry)" class="mt-4">
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
              :disabled="editSubmitting"
              @click="openEditModal(rightEntry)"
            >
              {{ $t('tournamentRoomPage.editEntry') }}
            </button>
          </div>
          <div class="mt-6 flex items-center justify-between gap-3">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ rightEntry.votes }} {{ $t('tournamentRoomPage.votes') }}</p>
            <button type="button" class="rounded-2xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] transition" :class="voteButtonClass(rightEntry)" :disabled="!canVote(rightEntry)" @click="castVote(rightEntry.id)">
              {{ voteButtonLabel(rightEntry) }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex min-h-[28rem] items-center justify-center rounded-[1.5rem] border border-dashed border-white/10 bg-white/[0.03] p-8 text-center text-sm text-steel-400">
            {{ $t('tournamentRoomPage.slotWaiting2') }}
          </div>
        </template>
      </div>
    </div>

    <div v-if="showEditModal" class="fixed inset-0 z-[140] overflow-y-auto bg-black/70 px-4 py-4 backdrop-blur-sm sm:px-6 sm:py-6" @click.self="closeEditModal">
      <div class="mx-auto flex min-h-full w-full max-w-xl items-center justify-center">
        <div class="flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-midnight-900/95 shadow-glow sm:max-h-[calc(100vh-3rem)]">
          <div class="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-5 sm:px-6">
            <div>
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentRoomPage.editEntry') }}</p>
              <h3 class="mt-1 font-display text-2xl font-semibold text-white">{{ editForm.carName || editableEntry?.carName || $t('tournamentRoomPage.roomFallback') }}</h3>
            </div>
            <button type="button" class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white" @click="closeEditModal">
              {{ $t('tournamentRoomPage.close') }}
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-5 py-5 pr-3 sm:px-6 sm:pr-4">
            <div class="grid gap-4">
              <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
                {{ $t('tournamentRoomPage.carName') }}
                <input v-model.trim="editForm.carName" type="text" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-400/30" />
              </label>
              <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
                {{ $t('tournamentRoomPage.buildSpecs') }}
                <textarea v-model.trim="editForm.specs" rows="4" class="mt-2 w-full resize-none rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-400/30"></textarea>
              </label>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10">
                <input class="absolute left-0 top-0 h-0 w-0 opacity-0" type="file" accept="image/*" @change="handleEditPhoto" />
                {{ $t('tournamentRoomPage.replacePhoto') }}
              </label>
              <div
                v-if="editForm.photoPreview"
                class="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-black"
                style="touch-action: none;"
                @pointerdown="startEditPreviewDrag($event)"
              >
                <img
                  :src="editForm.photoPreview"
                  alt="Tournament build edit preview"
                  class="h-full w-full select-none object-cover"
                  :style="{ objectPosition: editPreviewPosition }"
                  draggable="false"
                />
                <div class="pointer-events-none absolute inset-x-0 bottom-3 text-center text-[10px] uppercase tracking-[0.3em] text-white/70">
                  {{ $t('tournamentRoomPage.dragToPosition') }}
                </div>
              </div>
              <p v-else class="rounded-2xl border border-dashed border-white/10 bg-white/[0.03] px-4 py-6 text-center text-sm text-steel-400">
                {{ $t('tournamentRoomPage.noPhoto') }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-5 py-4 sm:px-6">
            <div class="mr-auto">
              <p v-if="editStatusMessage" class="text-sm text-emerald-100">{{ editStatusMessage }}</p>
              <p v-else class="text-xs text-steel-400">{{ $t('tournamentRoomPage.editHint') }}</p>
              <p v-if="editErrorMessage" class="mt-1 text-sm text-rose-200">{{ editErrorMessage }}</p>
            </div>
            <button type="button" class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10" :disabled="editSubmitting" @click="closeEditModal">
              {{ $t('tournamentRoomPage.cancel') }}
            </button>
            <button type="button" class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-70" :disabled="editSubmitting || !canSubmitEdit" @click="submitEdit">
              {{ editSubmitting ? $t('tournamentRoomPage.saving') : $t('tournamentRoomPage.saveChanges') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession } from '@/lib/supabase'

const ACTIVE_BRACKET_KEY = 'main'
const VOTE_TARGET = 5
const defaultEditForm = () => ({
  id: '',
  carName: '',
  specs: '',
  photoFile: null,
  photoPreview: '',
  photoPath: '',
  photoUrl: '',
})

export default {
  name: 'TournamentRoomDetailView',
  data() {
    return {
      room: null,
      entries: [],
      votes: [],
      sessionUser: null,
      currentProfile: null,
      errorMessage: '',
      statusMessage: '',
      showEditModal: false,
      editSubmitting: false,
      editForm: defaultEditForm(),
      editPhotoOffset: { x: 50, y: 50 },
      editPreviewDrag: null,
      editErrorMessage: '',
      editStatusMessage: '',
      liveChannel: null,
      authSubscription: null,
      visibilityHandler: null,
      refreshIntervalId: null,
    }
  },
  computed: {
    isAdmin() {
      return this.currentProfile?.role === 'admin'
    },
    leftEntry() {
      return this.entries.find((entry) => entry.slotNumber === 1) || null
    },
    rightEntry() {
      return this.entries.find((entry) => entry.slotNumber === 2) || null
    },
    currentUserVote() {
      const userId = this.sessionUser?.id
      if (!userId || !this.room?.id) return ''
      return this.votes.find((vote) => vote.room_id === this.room.id && vote.voter_id === userId)?.entry_id || ''
    },
    editableEntry() {
      const userId = this.sessionUser?.id
      if (!userId || this.room?.winner_entry_id) return null
      return this.entries.find((entry) => entry.userId === userId) || null
    },
    editPreviewPosition() {
      return `${this.editPhotoOffset.x}% ${this.editPhotoOffset.y}%`
    },
    canSubmitEdit() {
      return Boolean(this.editForm.id && this.editForm.carName && this.editForm.specs && this.editForm.photoPreview)
    },
    roomStatus() {
      if (!this.room) return this.$t('tournamentRoomPage.loadingRoom')
      if (this.room.winner_entry_id) {
        const winner = this.entries.find((entry) => entry.id === this.room.winner_entry_id)
        return winner ? `${winner.carName} already advanced from this room.` : this.$t('tournamentRoomPage.winnerDecided')
      }
      if (this.leftEntry && this.rightEntry) return `Voting live. First to ${VOTE_TARGET} votes advances.`
      return this.$t('tournamentRoomPage.waitingSlots')
    },
  },
  async mounted() {
    this.sessionUser = (await refreshReadySession())?.user || null
    await this.fetchCurrentProfile()
    await this.fetchRoom()
    this.setupRealtime()

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      this.sessionUser = session?.user || null
      await this.fetchCurrentProfile()
      await this.fetchRoom()
    })
    this.authSubscription = authListener?.subscription || null

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.recoverLiveData()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
    this.refreshIntervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.fetchRoom()
      }
    }, 15000)
  },
  beforeUnmount() {
    if (this.liveChannel) supabase.removeChannel(this.liveChannel)
    if (this.authSubscription) this.authSubscription.unsubscribe()
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.refreshIntervalId) {
      window.clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = null
    }
  },
  methods: {
    async recoverLiveData() {
      this.sessionUser = (await refreshReadySession())?.user || null
      await this.fetchCurrentProfile()
      await this.fetchRoom()
      this.setupRealtime()
    },
    async fetchCurrentProfile() {
      if (!this.sessionUser?.id) {
        this.currentProfile = null
        return
      }
      const { data } = await supabase
        .from('profiles')
        .select('id, role')
        .eq('id', this.sessionUser.id)
        .maybeSingle()
      this.currentProfile = data || null
    },
    async fetchRoom() {
      this.errorMessage = ''
      const roomId = this.$route.params.id

      const { data: room, error: roomError } = await supabase
        .from('tournament_rooms')
        .select('id, bracket_key, room_name, winner_entry_id, status')
        .eq('id', roomId)
        .eq('bracket_key', ACTIVE_BRACKET_KEY)
        .maybeSingle()

      if (roomError || !room) {
        this.errorMessage = roomError?.message || this.$t('tournamentRoomPage.roomNotFound')
        this.room = null
        this.entries = []
        this.votes = []
        return
      }

      this.room = room

      const [{ data: entries, error: entryError }, { data: votes, error: voteError }] = await Promise.all([
        supabase
          .from('tournament_entries')
          .select('id, user_id, slot_number, car_name, specs, photo_path, photo_url, photo_offset_x, photo_offset_y')
          .eq('room_id', room.id)
          .order('slot_number', { ascending: true }),
        supabase
          .from('tournament_votes')
          .select('id, room_id, entry_id, voter_id')
          .eq('room_id', room.id),
      ])

      if (entryError || voteError) {
        this.errorMessage = entryError?.message || voteError?.message || this.$t('tournamentRoomPage.loadError')
        return
      }

      const userIds = [...new Set((entries || []).map((entry) => entry.user_id).filter(Boolean))]
      let profilesById = new Map()
      if (userIds.length) {
        const { data: profiles } = await supabase.from('profiles').select('id, handle, full_name, avatar_url, avatar_path').in('id', userIds)
        profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]))
      }

      const voteCounts = new Map()
      ;(votes || []).forEach((vote) => {
        voteCounts.set(vote.entry_id, (voteCounts.get(vote.entry_id) || 0) + 1)
      })

      this.entries = (entries || []).map((entry) => {
        const profile = profilesById.get(entry.user_id) || {}
        const fullName = profile.full_name || this.$t('tournamentRoomPage.driverFallback')
        return {
          id: entry.id,
          userId: entry.user_id,
          slotNumber: entry.slot_number,
          displayName: fullName,
          handle: profile.handle || 'member',
          avatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          initials: fullName.split(' ').filter(Boolean).slice(0, 2).map((chunk) => chunk[0].toUpperCase()).join('') || 'D',
          carName: entry.car_name,
          specs: entry.specs,
          photoPath: entry.photo_path || '',
          photoUrl: entry.photo_url || '',
          photoOffsetX: Number.isFinite(entry.photo_offset_x) ? entry.photo_offset_x : 50,
          photoOffsetY: Number.isFinite(entry.photo_offset_y) ? entry.photo_offset_y : 50,
          photoPosition: `${Number.isFinite(entry.photo_offset_x) ? entry.photo_offset_x : 50}% ${Number.isFinite(entry.photo_offset_y) ? entry.photo_offset_y : 50}%`,
          photo: getPostImageUrl({ imagePath: entry.photo_path, imageUrl: entry.photo_url }),
          votes: voteCounts.get(entry.id) || 0,
        }
      })
      this.votes = votes || []
    },
    canEditEntry(entry) {
      if (!entry || !this.sessionUser?.id) return false
      if (this.isAdmin) return true
      if (this.room?.winner_entry_id) return false
      return entry.userId === this.sessionUser.id
    },
    openEditModal(entry = this.editableEntry) {
      if (!this.canEditEntry(entry)) return
      this.editErrorMessage = ''
      this.editStatusMessage = ''
      this.editForm = {
        id: entry.id,
        carName: entry.carName || '',
        specs: entry.specs || '',
        photoFile: null,
        photoPreview: entry.photo || '',
        photoPath: entry.photoPath || '',
        photoUrl: entry.photoUrl || '',
      }
      this.editPhotoOffset = {
        x: Number.isFinite(entry.photoOffsetX) ? entry.photoOffsetX : 50,
        y: Number.isFinite(entry.photoOffsetY) ? entry.photoOffsetY : 50,
      }
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editSubmitting = false
      this.editForm = defaultEditForm()
      this.editPhotoOffset = { x: 50, y: 50 }
      this.editPreviewDrag = null
      this.editErrorMessage = ''
      this.editStatusMessage = ''
    },
    handleEditPhoto(event) {
      const [file] = Array.from(event.target.files || [])
      if (!file) return
      this.editForm.photoFile = file
      this.editPhotoOffset = { x: 50, y: 50 }
      const reader = new FileReader()
      reader.onload = () => {
        this.editForm.photoPreview = typeof reader.result === 'string' ? reader.result : ''
      }
      reader.readAsDataURL(file)
      if (event?.target) event.target.value = ''
    },
    startEditPreviewDrag(event) {
      event.preventDefault()
      const target = event.currentTarget
      if (!target) return
      if (typeof target.setPointerCapture === 'function') {
        try {
          target.setPointerCapture(event.pointerId)
        } catch (e) {
          // ignore if capture fails
        }
      }
      const rect = target.getBoundingClientRect()
      this.editPreviewDrag = {
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: this.editPhotoOffset.x,
        startOffsetY: this.editPhotoOffset.y,
        width: rect.width || 1,
        height: rect.height || 1,
      }
      const onMove = (moveEvent) => {
        if (!this.editPreviewDrag) return
        moveEvent.preventDefault()
        const dx = moveEvent.clientX - this.editPreviewDrag.startX
        const dy = moveEvent.clientY - this.editPreviewDrag.startY
        const nextX = this.editPreviewDrag.startOffsetX + (dx / this.editPreviewDrag.width) * 100
        const nextY = this.editPreviewDrag.startOffsetY + (dy / this.editPreviewDrag.height) * 100
        this.editPhotoOffset = {
          x: Math.max(0, Math.min(100, Math.round(nextX))),
          y: Math.max(0, Math.min(100, Math.round(nextY))),
        }
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        this.editPreviewDrag = null
      }
      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', onUp)
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.82 }) {
      const objectUrl = URL.createObjectURL(file)
      try {
        const image = await new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = () => reject(new Error('Image decode failed.'))
          img.src = objectUrl
        })
        const scale = Math.min(1, maxWidth / (image.width || 1))
        const targetWidth = Math.max(1, Math.round((image.width || 1) * scale))
        const targetHeight = Math.max(1, Math.round((image.height || 1) * scale))
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight)
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
        if (!blob) throw new Error('Image conversion failed.')
        return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    },
    async uploadEditedPhoto() {
      const file = this.editForm.photoFile
      if (!file || !this.sessionUser?.id) return null
      const uploadFile = await this.convertImageToWebp(file, { maxWidth: 1600 })
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `tournament/${this.sessionUser.id}/${Date.now()}-${safeName}`
      const { error: uploadError } = await supabase.storage.from('posts').upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })
      if (uploadError) throw new Error('Photo upload failed. Make sure the "posts" bucket exists.')
      return { filePath, publicUrl: getPostImageUrl({ imagePath: filePath }) }
    },
    async submitEdit() {
      if (!this.canSubmitEdit) return
      this.editSubmitting = true
      this.editErrorMessage = ''
      this.editStatusMessage = ''
      try {
        let nextPhotoPath = this.editForm.photoPath
        let nextPhotoUrl = this.editForm.photoUrl
        if (this.editForm.photoFile) {
          const uploaded = await this.uploadEditedPhoto()
          nextPhotoPath = uploaded?.filePath || ''
          nextPhotoUrl = uploaded?.publicUrl || ''
        }
        const payload = {
          car_name: this.editForm.carName,
          specs: this.editForm.specs,
          photo_path: nextPhotoPath,
          photo_url: nextPhotoUrl,
          photo_offset_x: this.editPhotoOffset.x,
          photo_offset_y: this.editPhotoOffset.y,
        }
        const { error } = await supabase.rpc('update_tournament_entry_as_owner_or_admin', {
          p_entry_id: this.editForm.id,
          p_room_id: this.room.id,
          p_bracket_key: ACTIVE_BRACKET_KEY,
          p_car_name: payload.car_name,
          p_specs: payload.specs,
          p_photo_path: payload.photo_path,
          p_photo_url: payload.photo_url,
          p_photo_offset_x: payload.photo_offset_x,
          p_photo_offset_y: payload.photo_offset_y,
        })
        if (error) throw error
        this.statusMessage = this.$t('tournamentRoomPage.editSaved')
        await this.fetchRoom()
        this.closeEditModal()
      } catch (error) {
        this.editErrorMessage = error.message || this.$t('tournamentRoomPage.editSaveError')
      } finally {
        this.editSubmitting = false
      }
    },
    setupRealtime() {
      if (this.liveChannel) supabase.removeChannel(this.liveChannel)
      this.liveChannel = supabase
        .channel(`tournament-room-${this.$route.params.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_rooms' }, () => this.fetchRoom())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_entries' }, () => this.fetchRoom())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_votes' }, () => this.fetchRoom())
        .subscribe()
    },
    canVote(entry) {
      if (!entry || !this.sessionUser?.id) return false
      if (this.room?.winner_entry_id) return false
      if (!this.leftEntry || !this.rightEntry) return false
      if (entry.userId === this.sessionUser.id) return false
      return true
    },
    voteButtonClass(entry) {
      if (this.room?.winner_entry_id === entry.id) return 'bg-emerald-500 text-midnight-900'
      if (!this.canVote(entry)) return 'border border-white/10 bg-white/5 text-steel-400'
      return 'bg-white text-midnight-900 hover:bg-amber-300'
    },
    voteButtonLabel(entry) {
      if (this.room?.winner_entry_id === entry.id) return 'Advanced'
      if (!this.sessionUser?.id) return 'Login to vote'
      if (entry.userId === this.sessionUser.id) return 'Your build'
      if (!this.leftEntry || !this.rightEntry) return 'Waiting for opponent'
      if (this.currentUserVote === entry.id) return 'Your vote'
      if (this.currentUserVote) return 'Switch vote'
      return 'Vote'
    },
    async castVote(entryId) {
      this.errorMessage = ''
      this.statusMessage = ''
      if (!this.sessionUser?.id || !this.room?.id) {
        this.errorMessage = 'Please log in before voting.'
        return
      }
      const { data, error } = await supabase.rpc('cast_tournament_vote', {
        p_bracket_key: ACTIVE_BRACKET_KEY,
        p_room_id: this.room.id,
        p_entry_id: entryId,
      })
      if (error) {
        this.errorMessage = error.message || 'Unable to cast vote.'
        return
      }
      this.statusMessage = data?.[0]?.winner_entry_id ? 'Vote counted and winner advanced.' : 'Vote counted.'
      await this.fetchRoom()
    },
  },
}
</script>
